/**
 * Firebase Cloud Messaging (web): register device token in RTDB for server-side push.
 * Requires REACT_APP_FIREBASE_VAPID_KEY and public/firebase-messaging-sw.js (same Firebase config).
 */

import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { get, ref, set } from "firebase/database";
import { app, db } from "../auth/firebase";
import { env } from "../config/env";
import { getPublicSiteConfig } from "./platformConfigService";
import { showAppToast } from "../utils/appToastBus";

function shortKeyForToken(token) {
  let h = 0;
  for (let i = 0; i < token.length; i += 1) {
    h = (h << 5) - h + token.charCodeAt(i);
    h |= 0;
  }
  return `t${Math.abs(h)}`;
}

/**
 * Request notification permission, register SW, save FCM token under users/{uid}/fcmTokens/{id}.
 * Call after sign-in. No-op if VAPID key missing or browser unsupported.
 */
export async function registerFcmToken(uid) {
  if (typeof window === "undefined" || !uid || !env.vapidKey) return;

  try {
    const [site, prefsSnap] = await Promise.all([
      getPublicSiteConfig(),
      get(ref(db, `users/${uid}/notificationPrefs`)),
    ]);
    const prefs = prefsSnap.exists() ? prefsSnap.val() : {};
    if (site?.pushNotificationsEnabled === false || prefs?.pushEnabled === false) return;
  } catch {
    /* preference read should not block registration when the nodes are missing */
  }

  try {
    if (!(await isSupported())) return;
  } catch {
    return;
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return;

    const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    const messaging = getMessaging(app);
    const token = await getToken(messaging, {
      vapidKey: env.vapidKey,
      serviceWorkerRegistration: registration,
    });
    if (!token) return;

    const key = shortKeyForToken(token);

    await set(ref(db, `users/${uid}/fcmTokens/${key}`), {
      token,
      updatedAt: new Date().toISOString(),
      userAgent: typeof navigator !== "undefined" ? String(navigator.userAgent).slice(0, 240) : "",
    });
  } catch (err) {
    console.warn("[FCM] registration failed:", err?.message || err);
  }
}

/**
 * Foreground messages — show in-app toast (background handled by firebase-messaging-sw.js).
 * @returns {() => void} unsubscribe
 */
export function subscribeForegroundMessages() {
  if (typeof window === "undefined" || !env.vapidKey) return () => {};

  let messaging;
  try {
    messaging = getMessaging(app);
  } catch {
    return () => {};
  }

  return onMessage(messaging, (payload) => {
    const title = payload.notification?.title || "Swarand";
    const body = payload.notification?.body || payload.data?.body || "";
    if (body) {
      showAppToast({ severity: "info", summary: title, detail: body, life: 6000 });
    }
  });
}
